import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Building2, Upload, CheckCircle2, AlertCircle, Loader2, Camera } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CompanyProfilePage() {
  const { token, i18n } = useAuth();
  const [profile, setProfile] = useState(null);
  const [orgInfo, setOrgInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [logoPreview, setLogoPreview] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadProfile(); }, []);

  const loadProfile = async () => {
    try {
      const res = await axios.get(`${API}/company/profile`, { headers });
      setOrgInfo(res.data);
      setProfile(res.data.profile);
      setLogoPreview(res.data.profile.logo_url || null);
    } catch (e) { setError("Failed to load profile"); }
    setLoading(false);
  };

  const set = (k, v) => setProfile(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true); setSuccess(""); setError("");
    try {
      const { logo_url, ...data } = profile;
      await axios.put(`${API}/company/profile`, data, { headers });
      setSuccess("Profile saved successfully");
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) { setError(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setError("Logo must be under 2MB"); return; }
    // Preview
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
    // Upload
    const fd = new FormData(); fd.append("file", file);
    try {
      await axios.post(`${API}/company/logo`, fd, { headers });
      setSuccess("Logo uploaded");
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) { setError(e.response?.data?.detail || "Upload failed"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-amber-400 animate-spin" /></div>;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="company-profile-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-barlow font-bold text-2xl text-slate-50">Company Profile</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage your company identity and billing details</p>
        </div>
        <Badge className={`text-xs font-mono ${orgInfo?.plan === "ACTIVE" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-cyan-500/15 text-cyan-400 border-cyan-500/30"} border`}>
          {orgInfo?.plan}
        </Badge>
      </div>

      {success && <div className="p-3 rounded-lg border border-emerald-500/20 bg-emerald-500/5 flex items-center gap-2 text-emerald-400 text-sm" data-testid="profile-success"><CheckCircle2 className="w-4 h-4" />{success}</div>}
      {error && <div className="p-3 rounded-lg border border-red-500/20 bg-red-500/5 flex items-center gap-2 text-red-400 text-sm" data-testid="profile-error"><AlertCircle className="w-4 h-4" />{error}</div>}

      {/* Logo + Identity */}
      <Card className="bg-slate-900/40 border-slate-800/50">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-cyan-400 font-mono uppercase tracking-wider">Company Branding</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <div className="relative group">
              <div className="w-20 h-20 rounded-xl overflow-hidden bg-slate-800 border border-slate-700 flex items-center justify-center">
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-3xl font-bold text-amber-400">{profile?.display_name?.charAt(0) || "C"}</span>
                )}
              </div>
              <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 rounded-xl cursor-pointer transition-opacity">
                <Camera className="w-5 h-5 text-white" />
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleLogoUpload} data-testid="logo-upload-input" />
              </label>
            </div>
            <div>
              <h3 className="font-barlow font-bold text-lg text-slate-50">{profile?.display_name || orgInfo?.name}</h3>
              <p className="text-xs text-slate-500 mt-0.5">Upload PNG, JPG or WebP. Max 2MB.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Identity Fields */}
      <Card className="bg-slate-900/40 border-slate-800/50">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-cyan-400 font-mono uppercase tracking-wider">Company Identity</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] text-slate-500 uppercase">Legal Name</Label>
              <Input value={profile?.legal_name || ""} onChange={e => set("legal_name", e.target.value)} className="bg-slate-800/50 border-slate-700 mt-1" data-testid="profile-legal-name" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500 uppercase">Display / Trading Name</Label>
              <Input value={profile?.display_name || ""} onChange={e => set("display_name", e.target.value)} className="bg-slate-800/50 border-slate-700 mt-1" data-testid="profile-display-name" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] text-slate-500 uppercase">Country</Label>
              <Select value={profile?.country || ""} onValueChange={v => set("country", v)}>
                <SelectTrigger className="bg-slate-800/50 border-slate-700 mt-1" data-testid="profile-country"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="GB">United Kingdom</SelectItem><SelectItem value="US">United States</SelectItem>
                  <SelectItem value="AE">UAE</SelectItem><SelectItem value="DE">Germany</SelectItem>
                  <SelectItem value="FR">France</SelectItem><SelectItem value="ES">Spain</SelectItem>
                  <SelectItem value="AU">Australia</SelectItem><SelectItem value="SG">Singapore</SelectItem>
                  <SelectItem value="HK">Hong Kong</SelectItem><SelectItem value="JP">Japan</SelectItem>
                  <SelectItem value="IN">India</SelectItem><SelectItem value="NG">Nigeria</SelectItem>
                  <SelectItem value="ZA">South Africa</SelectItem><SelectItem value="KE">Kenya</SelectItem>
                  <SelectItem value="BR">Brazil</SelectItem><SelectItem value="MA">Morocco</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] text-slate-500 uppercase">Website</Label>
              <Input value={profile?.website || ""} onChange={e => set("website", e.target.value)} placeholder="https://..." className="bg-slate-800/50 border-slate-700 mt-1" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Business Information */}
      <Card className="bg-slate-900/40 border-slate-800/50">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-cyan-400 font-mono uppercase tracking-wider">Business Information</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-[10px] text-slate-500 uppercase">Company Address</Label>
            <Input value={profile?.address || ""} onChange={e => set("address", e.target.value)} className="bg-slate-800/50 border-slate-700 mt-1" data-testid="profile-address" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] text-slate-500 uppercase">Registration Number</Label>
              <Input value={profile?.registration_number || ""} onChange={e => set("registration_number", e.target.value)} className="bg-slate-800/50 border-slate-700 mt-1" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500 uppercase">VAT / Tax Number</Label>
              <Input value={profile?.vat_number || ""} onChange={e => set("vat_number", e.target.value)} className="bg-slate-800/50 border-slate-700 mt-1" data-testid="profile-vat" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Billing & Contact */}
      <Card className="bg-slate-900/40 border-slate-800/50">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-cyan-400 font-mono uppercase tracking-wider">Billing & Contact</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] text-slate-500 uppercase">Billing Email</Label>
              <Input type="email" value={profile?.billing_email || ""} onChange={e => set("billing_email", e.target.value)} className="bg-slate-800/50 border-slate-700 mt-1" data-testid="profile-billing-email" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500 uppercase">Phone</Label>
              <Input value={profile?.phone || ""} onChange={e => set("phone", e.target.value)} className="bg-slate-800/50 border-slate-700 mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] text-slate-500 uppercase">Primary Contact Name</Label>
              <Input value={profile?.contact_name || ""} onChange={e => set("contact_name", e.target.value)} className="bg-slate-800/50 border-slate-700 mt-1" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500 uppercase">Title / Role</Label>
              <Input value={profile?.contact_title || ""} onChange={e => set("contact_title", e.target.value)} placeholder="e.g. CFO, Director" className="bg-slate-800/50 border-slate-700 mt-1" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" className="border-slate-700" onClick={loadProfile}>Reset Changes</Button>
        <Button className="bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold px-8" onClick={handleSave} disabled={saving} data-testid="profile-save-btn">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Saving...</> : "Save Profile"}
        </Button>
      </div>
    </div>
  );
}
